import { useState, useRef, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Share2, MoreHorizontal, Loader2, Flag, UserPlus, UserCheck, User, Send, X, Mail, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { optimizeImg, mediaSrc } from "@/lib/img";
import { useAuth } from "@/context/AuthContext";
import LinkCard from "@/components/LinkCard";

const fmt = (n) => (n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, "") + "K" : `${n || 0}`);
const ago = (iso) => {
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now"; const m = s / 60; if (m < 60) return `${Math.floor(m)}m`;
  const h = m / 60; if (h < 24) return `${Math.floor(h)}h`; return `${Math.floor(h / 24)}d`;
};

function FeedVideo({ src, poster, postId }) {
  const ref = useRef(null);
  const counted = useRef(false);
  const userPaused = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    const v = ref.current; if (!v) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { if (!userPaused.current) v.play().catch(() => {}); if (!counted.current && postId) { counted.current = true; api.post(`/posts/${postId}/view`).catch(() => {}); } }
      else v.pause();
    }, { threshold: 0.5 });
    obs.observe(v); return () => obs.disconnect();
  }, [postId]);
  const togglePlay = (e) => {
    e.stopPropagation(); const v = ref.current; if (!v) return;
    if (v.paused) { userPaused.current = false; v.play().catch(() => {}); } else { userPaused.current = true; v.pause(); }
  };
  const toggleMute = (e) => {
    e.stopPropagation(); const v = ref.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
    if (!v.muted && v.paused) { userPaused.current = false; v.play().catch(() => {}); }
  };
  const btn = "absolute bottom-3 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center hover:bg-black/80 transition-colors";
  return (
    <div className="relative bg-black">
      <video ref={ref} src={src} poster={poster} muted={muted} loop playsInline preload="auto" onClick={togglePlay}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        className="w-full max-h-[70vh] object-contain bg-black cursor-pointer" data-testid="post-video" />
      <button onClick={togglePlay} data-testid="post-video-play-toggle" aria-label={playing ? "Pause" : "Play"} className={`${btn} left-3`}>
        {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}</button>
      <button onClick={toggleMute} data-testid="post-video-mute-toggle" aria-label={muted ? "Unmute" : "Mute"} className={`${btn} right-3`}>
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
    </div>
  );
}

function PostCard({ post, onDeleted }) {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes_count);
  const [shares, setShares] = useState(post.shares_count);
  const [cmts, setCmts] = useState(post.comments_count);
  const [following, setFollowing] = useState(post.following);
  const [menu, setMenu] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const isMe = user?.id === post.author.id;

  const like = async () => {
    setLiked((v) => !v); setLikes((n) => n + (liked ? -1 : 1));
    try { const { data } = await api.post(`/posts/${post.id}/like`); setLiked(data.liked); setLikes(data.likes_count); }
    catch { setLiked(post.liked); }
  };
  const share = async () => {
    try { const { data } = await api.post(`/posts/${post.id}/share`); setShares(data.shares_count);
      const url = `${window.location.origin}/post/${post.id}`;
      if (navigator.share) navigator.share({ url, title: "Nosey AI" }).catch(() => {});
      else { navigator.clipboard?.writeText(url); toast.success("Link copied"); }
    } catch { toast.error("Share failed"); }
  };
  const follow = async () => {
    setFollowing((v) => !v);
    try { const { data } = await api.post(`/users/${post.author.id}/follow`); setFollowing(data.following); }
    catch { setFollowing(following); }
  };
  const report = async () => {
    setMenu(false);
    try { await api.post(`/posts/${post.id}/report`, { reason: "Reported from feed" }); toast.success("Reported — thanks. Our team will review it."); }
    catch { toast.error("Report failed"); }
  };
  const del = async () => {
    setMenu(false);
    try { await api.delete(`/posts/${post.id}`); toast.success("Post deleted"); onDeleted && onDeleted(post.id); }
    catch { toast.error("Delete failed"); }
  };
  const goProfile = () => navigate(`/u/${post.author.id}`);
  const message = () => navigate(`/messages?to=${post.author.id}`);

  return (
    <article className="bg-ink-900/60 border border-white/10 rounded-2xl overflow-hidden cv-auto" data-testid={`post-${post.id}`}>
      <div className="flex items-center gap-3 px-4 pt-3.5 pb-2">
        <button onClick={goProfile} data-testid={`post-avatar-${post.id}`} className="w-10 h-10 rounded-full overflow-hidden bg-ink-800 shrink-0 border border-white/10">
          {post.author.avatar_url ? <img src={optimizeImg(post.author.avatar_url, 80, 70)} alt="" decoding="async" className="w-full h-full object-cover" /> : <User size={18} className="m-auto mt-2.5 text-zinc-500" />}
        </button>
        <div className="min-w-0 flex-1">
          <button onClick={goProfile} className="flex items-center gap-1.5">
            <span className="font-heading font-bold text-sm truncate">{post.author.username || post.author.name}</span>
            {post.author.plan && post.author.plan !== "free" && (
              <svg viewBox="0 0 24 24" width="14" height="14" className="shrink-0" style={{ filter: "drop-shadow(0 0 4px #22e06b)" }} data-testid="post-crown">
                <path d="M3 7l4 4 5-7 5 7 4-4-2 12H5L3 7z" fill="#22e06b" stroke="#22e06b" strokeWidth="0.5" />
              </svg>
            )}
          </button>
          <div className="text-[11px] text-zinc-500">{ago(post.created_at)}</div>
        </div>
        {!isMe && (
          <button onClick={follow} data-testid={`post-follow-${post.id}`}
            className={`text-xs font-heading font-bold rounded-full px-3 py-1 border transition-colors ${following ? "border-zinc-700 text-zinc-400" : "border-gold text-gold hover:bg-[#1a140d]"}`}>
            {following ? "Following" : "Follow"}
          </button>
        )}
        <div className="relative">
          <button onClick={() => setMenu((v) => !v)} data-testid={`post-menu-${post.id}`} className="text-zinc-400 hover:text-white p-1"><MoreHorizontal size={20} /></button>
          {menu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
              <div className="absolute right-0 top-8 z-20 w-44 bg-ink-800 border border-zinc-700 rounded-xl overflow-hidden shadow-2xl" data-testid={`post-menu-panel-${post.id}`}>
                {!isMe && <button onClick={() => { setMenu(false); follow(); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-ink-700 text-left">{following ? <UserCheck size={15} /> : <UserPlus size={15} />} {following ? "Unfollow" : "Follow"}</button>}
                <button onClick={() => { setMenu(false); goProfile(); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-ink-700 text-left"><User size={15} /> View Profile</button>
                {!isMe && <button onClick={() => { setMenu(false); message(); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-ink-700 text-left"><Mail size={15} /> Message</button>}
                {isMe ? <button onClick={del} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-ink-700 text-left text-red-400"><X size={15} /> Delete</button>
                  : <button onClick={report} data-testid={`post-report-${post.id}`} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-ink-700 text-left text-red-400"><Flag size={15} /> Report</button>}
              </div>
            </>
          )}
        </div>
      </div>

      {post.caption && <p className="px-4 pb-3 text-sm leading-relaxed whitespace-pre-wrap">{post.caption}</p>}
      {post.media_type === "link" ? <LinkCard link={post.link} />
        : <div className="bg-black">
            {post.media_type === "video" ? <FeedVideo src={mediaSrc(post.media_url)} poster={mediaSrc(post.thumb_url)} postId={post.id} />
              : <img src={optimizeImg(post.media_url, 800, 72)} alt="" decoding="async" className="w-full max-h-[75vh] object-contain" />}
          </div>}

      <div className="flex items-center gap-6 px-4 py-3">
        <button onClick={like} data-testid={`post-like-${post.id}`} className="flex items-center gap-2 text-sm">
          <Heart size={22} className={liked ? "fill-red-500 text-red-500" : "text-zinc-300"} /> <span className="text-zinc-300">{fmt(likes)}</span>
        </button>
        <button onClick={() => setShowComments(true)} data-testid={`post-comment-${post.id}`} className="flex items-center gap-2 text-sm text-zinc-300"><MessageCircle size={22} /> {fmt(cmts)}</button>
        <button onClick={share} data-testid={`post-share-${post.id}`} className="flex items-center gap-2 text-sm text-zinc-300"><Share2 size={22} /> {fmt(shares)}</button>
        <button onClick={message} className="ml-auto flex items-center gap-1.5 text-sm text-zinc-300 hover:text-gold"><Mail size={20} /></button>
      </div>

      {showComments && <CommentsSheet post={post} onClose={() => setShowComments(false)} onAdded={() => { setCmts((n) => n + 1); refreshUser(); }} />}
    </article>
  );
}

export default memo(PostCard);

function CommentsSheet({ post, onClose, onAdded }) {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const isMe = user?.id === post.author.id;
  useEffect(() => { api.get(`/posts/${post.id}/comments`).then(({ data }) => setList(data)).catch(() => setList([])); }, [post.id]);
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  const send = async () => {
    if (!text.trim()) return; setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { text });
      setList((l) => [{ id: data.id, text: data.text, created_at: data.created_at, author: data.author }, ...(l || [])]);
      setText(""); onAdded();
      if (!isMe) toast.success("Comment posted · 2 coins");
    } catch (e) {
      if (e.response?.status === 402) toast.error("Not enough coins — buy coins to comment");
      else toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post comment");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid={`comments-${post.id}`}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-ink-900 border-t sm:border border-zinc-800 sm:rounded-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="font-heading font-bold">Comments</span>
          <button onClick={onClose} data-testid={`comments-close-${post.id}`} className="text-zinc-400"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[120px]">
          {list === null ? <Loader2 className="animate-spin text-gold mx-auto" size={22} />
            : list.length === 0 ? <p className="text-center text-sm text-zinc-600 py-6">Be the first to comment.</p>
            : list.map((c) => (
              <div key={c.id} className="flex gap-2.5">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-ink-800 shrink-0">{c.author.avatar_url ? <img src={c.author.avatar_url} className="w-full h-full object-cover" alt="" /> : <User size={14} className="m-auto mt-2 text-zinc-500" />}</div>
                <div className="min-w-0"><span className="text-xs font-heading font-bold">{c.author.name}</span><p className="text-sm text-zinc-300">{c.text}</p></div>
              </div>
            ))}
        </div>
        <div className="p-3 border-t border-white/10">
          {!isMe && <p className="text-[11px] text-zinc-500 mb-1.5">Commenting costs 2 coins (1 goes to the creator).</p>}
          <div className="flex items-center gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid={`comment-input-${post.id}`}
              placeholder="Add a comment…" className="flex-1 bg-ink-950 border border-zinc-800 rounded-full px-4 py-2 text-sm focus:border-gold focus:outline-none" />
            <button onClick={send} disabled={busy} data-testid={`comment-send-${post.id}`} className="w-10 h-10 rounded-full bg-gold text-ink-950 flex items-center justify-center disabled:opacity-50">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
